import { Injectable, NgZone, TemplateRef } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subject,
  catchError,
  combineLatest, debounceTime,
  distinctUntilChanged, filter,
  fromEvent, map,
  of,
  switchMap,
  take,
  takeUntil
} from 'rxjs';

import { MatDrawerMode } from '@angular/material/sidenav';
import { filterAllTruthyInTuple, subjectFromObs } from 'core/rx-js';
import { ScreenUtilsService } from 'core/services';

import { IPdfDocument, IPdfDocumentInput } from './types';

type DocumentPageSpans = { [docId: string]: { start: number, end: number } }

export interface IPageInfo {
  docId: string,
  pageIdx: number,
  realPageIdx: number
  dimensions: { height: number, width: number },
  scrollTop: number,
  base64png?: string,
  scale: number
}

export type OnDocumentDisplayedFn = (document: IPdfDocumentInput) => any

@Injectable()
export class PdfViewerService {

  private readonly _destroy = new Subject<void>();

  readonly PAGE_SPACING = 10;
  readonly PAGE_BUFFER = 3;
  readonly PAGE_BATCH_SIZE = 4;
  readonly THUMBNAIL_WIDTH = 125;

  readonly scrollTopSubject = new BehaviorSubject<number>(0);
  readonly scrollerSubject = new BehaviorSubject<HTMLDivElement>(undefined);

  readonly renderedBatchesSubject = new BehaviorSubject<number[]>([]);
  readonly toolbarItemTemplateSubject = new BehaviorSubject<TemplateRef<any>>(null)
  readonly sidebarOpenedSubject = new BehaviorSubject<boolean>(true);
  readonly selectedZoomSubject = new BehaviorSubject<number>(100);
  readonly inPageWidthModeSubject = new BehaviorSubject<boolean>(false);
  readonly scrollableWidthSubject = new BehaviorSubject<number>(100);


  readonly addedPageContentSubject = new BehaviorSubject<TemplateRef<any>>(undefined)
  readonly screenSizeSubject = this._screenUtilsService.screenSizeSubject;

  private readonly _drawerModeObs = this.screenSizeSubject.pipe(
    map((screenSize) => screenSize === 'sm' || screenSize === 'md' || screenSize === 'xs'),
    distinctUntilChanged(),
    map((smallScreen) => smallScreen ? 'over' : 'side'),
  )

  readonly drawerModeSubject = subjectFromObs<MatDrawerMode>({
    observable: this._drawerModeObs,
    initialValue: 'side',
    until: this._destroy
  })

  readonly pdfDocumentInputsSubject = new BehaviorSubject<IPdfDocumentInput[]>(undefined);
  readonly pdfPageInfosSubject = new BehaviorSubject<IPageInfo[]>(undefined);
  get pdfPageInfos() {
    return this.pdfPageInfosSubject.value;
  }


  readonly pageIdxSubject = subjectFromObs({
    initialValue: 1,
    observable: combineLatest([
      this.scrollTopSubject,
      this.pdfPageInfosSubject
    ])
      .pipe(
        debounceTime(10),
        map(([scrollTop, pdfPageInfos]) => {

          if (scrollTop === 0) {
            return 1;
          }

          const page = pdfPageInfos.find((page) => (page.scrollTop + page.dimensions.height) > scrollTop);
          if (!page) {
            return pdfPageInfos[pdfPageInfos.length - 1].pageIdx
          }
          return page.pageIdx;

        }),
        debounceTime(100),
        distinctUntilChanged(),
      ),
    until: this._destroy
  });

  readonly maxPageRangeObs = this.pdfPageInfosSubject.pipe(
    filter(Boolean),
    map((pdfPageInfos) => ({ min: 1, max: pdfPageInfos.length }))
  )

  readonly renderedRangeObs = combineLatest([this.pageIdxSubject, this.maxPageRangeObs])
    .pipe(
      filterAllTruthyInTuple(),
      map(([pageIdx, maxPageRange]) => ({
        start: Math.max(maxPageRange.min, pageIdx - this.PAGE_BUFFER),
        end: Math.min(maxPageRange.max, pageIdx + this.PAGE_BUFFER)
      }))
    )

  readonly renederedBatchIdxsObs = this.renderedRangeObs.pipe(
    filter(Boolean),
    map(({ start, end }) => {

      const startBatch = Math.floor(start / this.PAGE_BATCH_SIZE);
      const endBatch = Math.floor(end / this.PAGE_BATCH_SIZE);

      const renderedBatchArray: number[] = []

      for (let idx = startBatch; idx <= endBatch; idx++) {
        renderedBatchArray.push(idx)
      }

      return renderedBatchArray;
    })
  )

  readonly selectedPdfSubject = new BehaviorSubject<IPdfDocument>(undefined);
  readonly selectedPageSubject = new BehaviorSubject<{ pageIdx: number, source?: 'pdfScroll' | 'thumbnailSelect' }>({ pageIdx: 1 });

  readonly selectedPageIdxObs = this.selectedPageSubject
    .pipe(
      map(selectedPage => selectedPage.pageIdx),
      distinctUntilChanged()
    )
  readonly pagesSubject = new BehaviorSubject<number[]>([]);

  readonly pageLoadFnSubject = new BehaviorSubject<(batchNo: number, batchSize: number) => Observable<string[]>>(null);

  readonly selectedPageInfoObs = combineLatest([this.pageIdxSubject, this.pdfPageInfosSubject]).pipe(
    filter(([_, pdfPageInfos]) => !!pdfPageInfos),
    map(([pageIdx, pdfPageInfos]) => pdfPageInfos.find((pdfInfo) => pdfInfo.pageIdx === pageIdx)),
    distinctUntilChanged()
  )

  readonly selectedPageInfoSubject = subjectFromObs({
    observable: this.selectedPageInfoObs,
    until: this._destroy,
    initialValue: undefined
  })



  readonly selectedDocumentObs = combineLatest([this.selectedPageInfoSubject, this.pdfDocumentInputsSubject])
    .pipe(
      filterAllTruthyInTuple(),
      map(([selectedPageInfo, pdfDocumentInputs]) => pdfDocumentInputs.find(({ id }) => id === selectedPageInfo.docId))
    )
  constructor(
    private readonly _screenUtilsService: ScreenUtilsService,
    private readonly _ngZone: NgZone
  ) {



    setTimeout(() => {
      this.initialize();
    },
      100
    )

    this.drawerModeSubject.pipe(
      filter((mode) => mode === 'over'),
      takeUntil(this._destroy)
    )
      .subscribe(() => {
        this.sidebarOpenedSubject.next(false);
      })

  }

  readonly pageBase64pngsSubject = new BehaviorSubject<{ [pageIdx: number]: string }>({})
  get pageBase64pngs() {
    return this.pageBase64pngsSubject.value;
  }

  readonly pagesLoadedObs = this.pageBase64pngsSubject.pipe(
    map((pageBase64pngs) => !!Object.values(pageBase64pngs || {}).length),
    debounceTime(250)
  )

  initialize() {
    const start = new Date().getTime() / 1000;

    const timestamps: any = {
      docsLoaded: null,
      pagesLoaded: null
    }

    this.scrollerSubject.pipe(
      filter(Boolean),
      take(1),
      takeUntil(this._destroy),
      switchMap((scroller) => fromEvent(scroller, 'scroll').pipe(map(() => scroller))),
      map((scroller) => Math.round(scroller.scrollTop)),
      filter((scrollTop) => Math.abs(this.scrollTopSubject.value - scrollTop) > 2),
      distinctUntilChanged()
    )
      .subscribe((scrollTop) => {
        this.scrollTopSubject.next(scrollTop)
      })

    this.pdfDocumentInputsSubject.pipe(
      takeUntil(this._destroy)
    )
      .subscribe(() => {
        this.pageBase64pngsSubject.next({});
        this.renderedBatchesSubject.next([]);
      })

    combineLatest([this.renederedBatchIdxsObs, this.pageLoadFnSubject, this.pdfDocumentInputsSubject])
      .pipe(
        debounceTime(250),
        this.loadPages(),
        takeUntil(this._destroy)
      )
      .subscribe(({ pageBase64pngs, prevRenderedBatches }) => {

        this.pageBase64pngsSubject.next(pageBase64pngs);
        this.renderedBatchesSubject.next(prevRenderedBatches)
      })


    combineLatest([this.pdfDocumentInputsSubject, this.selectedZoomSubject, this.inPageWidthModeSubject, this.scrollableWidthSubject]).pipe(
      map(([pdfDocumentInputs, selectedZoom, inPageWidthMode, scrollableWidth]) => ({ pdfDocumentInputs, selectedZoom, inPageWidthMode, scrollableWidth })),
      filter(({ pdfDocumentInputs }) => Boolean(pdfDocumentInputs)),
      distinctUntilChanged((prev, next) => {

        if (prev.inPageWidthMode !== next.inPageWidthMode || prev.pdfDocumentInputs !== next.pdfDocumentInputs) {
          return false;
        }

        if (next.inPageWidthMode) {
          return prev.scrollableWidth === next.scrollableWidth;
        }

        return prev.selectedZoom === next.selectedZoom;
      }),
      map(({ inPageWidthMode, pdfDocumentInputs, scrollableWidth, selectedZoom }) => {

        if (inPageWidthMode) {
          return this.getPageInfos({
            pdfDocumentInputs,
            scaleToWidth: scrollableWidth - 50
          })
        }

        return this.getPageInfos({
          pdfDocumentInputs,
          scaleByFactor: selectedZoom / 100
        })
      }),
      takeUntil(this._destroy)
    )
      .subscribe((pdfPageInfos) => {
        this.pdfPageInfosSubject.next(pdfPageInfos);

        timestamps.pagesLoaded = new Date().getTime() / 1000 - start
      })

  }

  readonly loadPages = () =>
    (obs: Observable<[number[], (batchNo: number, batchSize: number) => Observable<string[]>, any]>) =>
      obs.pipe(
        filterAllTruthyInTuple(),
        debounceTime(100),
        distinctUntilChanged(([prevRenderedBatches, prevFn, prevPages], [nextRenderedBatches, nextFn, nextPages]) => {
          return JSON.stringify(prevRenderedBatches) === JSON.stringify(nextRenderedBatches)
            && prevFn === nextFn
            && nextPages === prevPages
        }

        ),
        switchMap(([renderedBatches, pageLoadFn]) =>
          combineLatest(renderedBatches
            .filter((batchNo) => !this.renderedBatchesSubject.value.includes(batchNo))
            .map((batchNo) => pageLoadFn(batchNo, this.PAGE_BATCH_SIZE)
              .pipe(
                catchError(() => pageLoadFn(batchNo, this.PAGE_BATCH_SIZE)),
                map((renders) => ({ batchNo, renders })),
                catchError(() => of(null))
              )
            )
          )
        ),
        map((result) => result.filter(Boolean)),
        map(result => {
          const prevRenderedBatches = this.renderedBatchesSubject.value;

          result.forEach(({ batchNo, renders }) => {
            prevRenderedBatches.push(batchNo);
            renders.forEach((base64png, renderIdx) => {
              const idx = renderIdx + batchNo * this.PAGE_BATCH_SIZE;
              this.pageBase64pngs[idx + 1] = base64png;
            })
          })


          return { prevRenderedBatches, pageBase64pngs: this.pageBase64pngs }
        })
      )

  getDocumentIdFromPage(pageIdx: number): string | null {

    const page = this.pdfPageInfosSubject.value.find((pageInfo) => pageInfo.pageIdx === pageIdx)
    return page.docId;
  }


  get selectedPageInfo() {
    return this.selectedPageInfoSubject.value;
  }

  readonly realPageIdxSubject = subjectFromObs({
    observable: this.selectedPageInfoObs
      .pipe(
        filter(Boolean),
        map((selectedPage) => selectedPage.realPageIdx)
      ),
    initialValue: 1
  })

  get realPageIdx() {
    return this.realPageIdxSubject.value
  }


  getPageInfos(params: {
    pdfDocumentInputs: IPdfDocumentInput[],
    docTitleHeight?: number,
    pageSpacing?: number,
    scaleToWidth?: number,
    scaleByFactor?: number
  }) {

    let {
      pdfDocumentInputs,
      docTitleHeight,
      pageSpacing,
      scaleToWidth,
      scaleByFactor
    } = params;

    docTitleHeight = docTitleHeight || 80;
    pageSpacing = pageSpacing || this.PAGE_SPACING;

    let pagesOffset = 0;
    let scrollTop = 0;
    const pageInfos = pdfDocumentInputs.reduce<IPageInfo[]>((total, current, docIdx, arr) => {
      const { id, documentMetadata } = current;

      for (let inDocIdx = 1; inDocIdx <= documentMetadata.length; inDocIdx++) {

        const pageMetadata = documentMetadata[inDocIdx - 1];
        const { width, height, scale } = this.scaleDimensions(pageMetadata, scaleToWidth, scaleByFactor);

        total.push({
          docId: id,
          pageIdx: inDocIdx + pagesOffset,
          realPageIdx: inDocIdx,
          dimensions: { width, height },
          scrollTop: scrollTop + (docIdx + 1) * docTitleHeight - docIdx * pageSpacing,
          scale
        })

        scrollTop += height + pageSpacing;
      }

      pagesOffset += documentMetadata.length;
      return total;
    }
      ,
      []
    );

    return pageInfos;
  }

  scaleDimensions(dimension: { width: number, height: number }, scaleToWidth?: number, scaleByFactor?: number) {

    if (scaleByFactor) {

      return {
        width: Math.round(scaleByFactor * dimension.width),
        height: Math.round(scaleByFactor * dimension.height),
        scale: scaleByFactor
      }
    }


    if (scaleToWidth) {

      const scale = scaleToWidth / dimension.width;

      return {
        width: scaleToWidth,
        height: Math.round(scale * dimension.height),
        scale: scale
      }

    }

    return {
      height: Math.round(dimension.height),
      width: Math.round(dimension.width),
      scale: 1
    }

  }

  ngOnDestroy() {

    // this.pdfDocumentsSubject.complete();
    // this.pdfDocumentDtosSubject.complete();
    // this._subscriptionHandler.dispose();

    this._destroy.next();
    this._destroy.complete();

    this.sidebarOpenedSubject.complete();
    this.selectedZoomSubject.complete();
    this.addedPageContentSubject.complete();
    this.selectedPdfSubject.complete();
    this.selectedPageSubject.complete();
    this.pagesSubject.complete();

  }




}
